import { BrowserRouter as Router, Switch, Route, Redirect } from "react-router-dom";

import Cart from "./pages/Cart";
import CartHub from "./pages/CartHub";
import Policy from "./pages/Policy";
import ReturnPolicy from "./pages/ReturnPolicy";
import Contact from "./pages/Contact";
import PaymentScreen from "./pages/PaymentScreen";
import PlaceOrderScreen from "./pages/PlaceOrderScreen";
import Product from "./pages/Product";
import PromoProductDetailsPage from "./pages/PromoProductDetailsPage";
import ProductList from "./pages/ProductList";
import ShopCard from "./pages/ShopCard";
import Success from "./pages/Success";
import CODOrderScreen from "./pages/CODOrderScreen";
import AirtelPayScreen from "./pages/AirtelPayScreen";
import MomoOrderScreen from "./pages/MomoOrderScreen";
import ProtectedRoute from "./components/ProtectedRoute";
import AuthRoute from "./components/AuthRoute";
import Register from "./pages/SignUp/Register";
import Login from "./pages/Login/Login";
import WebClientPasswordResetForm from "./pages/Login/WebClientPasswordResetForm";
import ForgotPasswordPage from "./pages/Login/ForgotPasswordPage";
import ShippingScreen from "./pages/Shipping/ShippingScreen";
import RestaurantShippingScreen from "./pages/Shipping/RestaurantShippingScreen";
import RetailerWholesalerShippingScreen from "./pages/Shipping/RetailerWholesalerShippingScreen";
import MyProfile from "./pages/MyProfile";
import MyAddresses from "./pages/MyAddresses";
import OrderDetail from "./components/Order/OrderDetail";
import Promotions from "./components/Promotions/Promotions";
import Receipts from "./components/Receipts/Receipts";
import { ToastContainer } from "react-toastify";
import UserProfile from "./components/UserProfile/UserProfile";
import UserOrders from "./components/UserOrders/UserOrders";
import useTokenExpiry from "./hooks/useTokenExpiry";
import CardPinForm from "./components/CreditCardForm/Pin/CardPinForm";
import CardPaymentDoneScreen from "./pages/CardPaymentDoneScreen";
import EventsPage from "./pages/EventsPage";
import EventDetailPage from "./pages/EventDetailPage";
import SeatSelectionPage from "./pages/SeatSelectionPage";
import TicketCheckoutPage from "./pages/TicketCheckoutPage";
import MyTicketsPage from "./pages/MyTicketsPage";
import TicketQRPage from "./pages/TicketQRPage";
import TicketPaymentReturnPage from "./pages/TicketPaymentReturnPage";
import CardOtpForm from "./components/CreditCardForm/Pin/CardOtpForm";
import LandingPage from "./pages/LandingPage";
import AboutUs from "./pages/AboutUs";
import DeleteAccount from "./pages/DeleteAccount";
import AllRestaurantsPage from "./pages/AllRestaurantsPage";
import AllHealthcarePage from "./pages/AllHealthcarePage";
import AllVendorsPage from "./pages/AllVendorsPage";
import PaymentOptions from "./pages/PaymentOptions";
import ShippingMatrix from "./pages/ShippingMatrix";
// import RestaurantMenu from "./components/RestaurantMenu";
import RestaurantPaymentScreen from "./pages/RestaurantPaymentScreen";
import WholesalerRetailerPaymentScreen from "./pages/WholesalerRetailerPaymentScreen";
import RestaurantPlaceOrderScreen from "./pages/RestaurantPlaceOrderScreen";
import RetailerWholesalerPlaceOrderScreen from "./pages/RetailerWholesalerPlaceOrderScreen";
import VendorShopPage from "./pages/VendorShopPage";
import RetailerWholesalerProductPage from "./pages/RetailerWholesalerProductPage";
import RestaurantShopPage from "./pages/RestaurantShopPage";
import CODRetailerWholesalerOrderScreen from "./pages/CODRetailerWholesalerOrderScreen";
import DeepLinkHandler from "./DeepLinkHandler";
import ScrollToTop from "./components/ScrollToTop";
import ImageGuard from "./components/ImageGuard";
import HealthcareShopPage from "./pages/HealthcareShopPage";
import HealthcareShippingScreen from "./pages/Shipping/HealthcareShippingScreen";
import HealthcarePaymentScreen from "./pages/HealthcarePaymentScreen";
import HealthcarePlaceOrderScreen from "./pages/HealthcarePlaceOrderScreen";
import HealthcareOrderScreen from "./pages/HealthcareOrderScreen";
import BrandShopPage from "./pages/BrandShopPage";
import BrandProductPage from "./pages/BrandProductPage";
import AllBrandsPage from "./pages/AllBrandsPage";
import BrandShippingScreen from "./pages/Shipping/BrandShippingScreen";
import BrandPaymentScreen from "./pages/BrandPaymentScreen";
import BrandPlaceOrderScreen from "./pages/BrandPlaceOrderScreen";
import SearchResultsPage from "./pages/SearchResultsPage";
import CreatePackagePage from "./pages/CreatePackagePage";
import OAuthConsentPage from "./pages/OAuthConsentPage";
import BlogListPage from "./pages/BlogListPage";
import BlogArticlePage from "./pages/BlogArticlePage";
import OmnichannelMessagingPage from "./pages/OmnichannelMessagingPage";
import SupportCasesPage from "./pages/SupportCasesPage";
import SupportCaseDetailPage from "./pages/SupportCaseDetailPage";
import { GoogleMapsProvider } from "./contexts/GoogleMapsContext";
import useAnalyticsUser from "./hooks/useAnalyticsUser";
import useLocationSetTracking from "./hooks/useLocationSetTracking";
import ReferralCapture from "./components/ReferralCapture";
import GroupBasketJoinPage from "./pages/GroupBasketJoinPage";
import GroupBasketPage from "./pages/GroupBasketPage";

// NOTE: page_view tracking is owned entirely by GA4 itself — the gtag config
// sends the initial page_view and Enhanced Measurement ("page changes based on
// browser history events") sends one per SPA route change. The app deliberately
// does NOT send manual page_views, to avoid double-counting. Custom events
// (vendor_view, view_item, add_to_cart, …) are still sent via react-ga4.

const App = () => {
  // Monitor token expiry and auto-logout
  useTokenExpiry();
  // GA4 User-ID follows the signed-in account (web and apps share it).
  useAnalyticsUser();
  // location_set fires once per real change of delivery area.
  useLocationSetTracking();

  return (
    <GoogleMapsProvider>
      <Router>
        <ScrollToTop />
        {/* Keeps a friend's ?ref= invite code until account creation. */}
        <ReferralCapture />
        {/* Site-wide no-easy-download net for vendor artwork; see the file. */}
        <ImageGuard />
        <Switch>
        <Route exact path="/" component={LandingPage} />
        <Route exact path="/about" component={AboutUs} />
        {/* The account deletion URL declared in both apps' Play Data safety
            forms. Must stay public, must stay at this exact path. */}
        <Route exact path="/delete-account" component={DeleteAccount} />
        <Route exact path="/omnichannel-inbox" component={OmnichannelMessagingPage} />
        <Route exact path="/restaurants" component={AllRestaurantsPage} />
        <Route exact path="/healthcare" component={AllHealthcarePage} />
        <Route exact path="/vendors" component={AllVendorsPage} />
        <Route exact path="/brands" component={AllBrandsPage} />
        {/* <Route path="/restaurant/menu/:restaurantId" component={RestaurantMenu} /> */}
        {/* Dedicated product page — must sit ABOVE the non-exact
            /shop/:vendorId route or it would never match. */}
        <Route
          path="/shop/:vendorId/product/:productId"
          component={RetailerWholesalerProductPage}
        />
        <Route path="/shop/:vendorId" component={VendorShopPage} />
        <Route path="/restaurant/shop/:id" component={RestaurantShopPage} />
        {/* Order together (group baskets). The link a host shares opens the
            join page, signed in or not; the basket itself is for members. */}
        <Route exact path="/g/:token" component={GroupBasketJoinPage} />
        <ProtectedRoute exact path="/group-basket/:basketId" component={GroupBasketPage} />

        {/* Healthcare Routes */}
        {/* A pharmacy product's own address: the storefront with that
            product open. Must sit ABOVE /healthcare/shop/:id, which is not
            exact and would match it with the product part ignored. Same
            component, so a tap that changes the address does not remount
            or refetch the storefront. */}
        <Route
          path="/healthcare/shop/:id/product/:productId"
          component={HealthcareShopPage}
        />
        <Route path="/healthcare/shop/:id" component={HealthcareShopPage} />
        <ProtectedRoute path="/healthcare/shipping/:vendorId" component={HealthcareShippingScreen} />
        <ProtectedRoute path="/healthcare/payment/:vendorId" component={HealthcarePaymentScreen} />
        <ProtectedRoute path="/healthcare/placeorder/:vendorId" component={HealthcarePlaceOrderScreen} />
        <ProtectedRoute path="/healthcareOrderPage" component={HealthcareOrderScreen} />

        {/* Brand Routes */}
        {/* Shareable, cold-load-safe product URL. Must sit ABOVE
            /brand/shop/:id or that non-exact route swallows it — the same
            ordering trap as the retailer product route above.

            It carries BOTH ids because the only brand product lookup on the
            API is nested (GET /api/brands/:brandId/products/:productId), so a
            productId alone cannot be resolved by someone arriving cold from a
            QR code or a shared link. */}
        <Route
          path="/brand/shop/:brandId/product/:productId"
          component={BrandProductPage}
        />
        <Route path="/brand/shop/:id" component={BrandShopPage} />
        {/* Legacy in-app route: works only when pushed with router state. */}
        <Route path="/brand/product/:productId" component={BrandProductPage} />
        <ProtectedRoute
          exact
          path="/brand-shipping"
          component={BrandShippingScreen}
        />
        <ProtectedRoute
          exact
          path="/brand-payment"
          component={BrandPaymentScreen}
        />
        <ProtectedRoute
          exact
          path="/brand-placeorder/:id"
          component={BrandPlaceOrderScreen}
        />

        {/* Package Delivery Route */}
        <ProtectedRoute
          exact
          path="/package-delivery"
          component={CreatePackagePage}
        />

        {/* Deep Linking for the Mobile App */}
        <Route path="/restaurant/:restaurantId" component={DeepLinkHandler} />

        <Route exact path="/search" component={SearchResultsPage} />
        <Route exact path="/shop" component={ShopCard} />
        <Route path="/products/:category" component={ProductList} />
        <Route path="/product/:id" component={Product} />
        <Route path="/promoProduct/:id" component={PromoProductDetailsPage} />
        <Route path="/cart" component={CartHub} />
        <Route exact path="/events" component={EventsPage} />
        <Route path="/event/:eventId" component={EventDetailPage} />
        <Route path="/showing/:showingId" component={SeatSelectionPage} />
        <ProtectedRoute path="/ticket-checkout/:holdId" component={TicketCheckoutPage} />
        <ProtectedRoute path="/ticket-paid/:orderId" component={TicketPaymentReturnPage} />
        <ProtectedRoute exact path="/tickets" component={MyTicketsPage} />
        <ProtectedRoute path="/ticket/:ticketId" component={TicketQRPage} />
        <Route path="/cart-legacy" component={Cart} />
        <Route path="/policy" component={Policy} />
        <Route path="/return-policy" component={ReturnPolicy} />
        <Route path="/contact" component={Contact} />
        <Route path="/payment_options" component={PaymentOptions} />
        <Route path="/shipping_matrix" component={ShippingMatrix} />
        <Route path="/orderPage" component={CODOrderScreen} />
        <Route
          path="/rworderPage"
          component={CODRetailerWholesalerOrderScreen}
        />
        <Route path="/airtelPay" component={AirtelPayScreen} />
        <Route path="/momoOrderPage" component={MomoOrderScreen} />
        <Route path="/success" component={Success} />
        <AuthRoute path="/login" component={Login} />
        <AuthRoute path="/register" component={Register} />
        <AuthRoute
          path="/forgot-password"
          component={ForgotPasswordPage}
        />
        {/* Legacy route - kept for backwards compatibility with old email links */}
        <AuthRoute
          path="/passwordResetReq"
          component={ForgotPasswordPage}
        />
        <AuthRoute
          path="/pwdResetForm/:token/:userId"
          component={WebClientPasswordResetForm}
        />
        <ProtectedRoute exact path="/shipping" component={ShippingScreen} />
        <ProtectedRoute
          exact
          path="/restaurant-shipping"
          component={RestaurantShippingScreen}
        />
        <ProtectedRoute
          exact
          path="/retailer-wholesaler-shipping"
          component={RetailerWholesalerShippingScreen}
        />
        <ProtectedRoute
          exact
          path="/retailer-wholesaler-payment"
          component={WholesalerRetailerPaymentScreen}
        />

        <ProtectedRoute exact path="/payment" component={PaymentScreen} />
        <ProtectedRoute
          exact
          path="/restaurant-payment"
          component={RestaurantPaymentScreen}
        />
        <ProtectedRoute exact path="/placeOrder" component={PlaceOrderScreen} />
        <ProtectedRoute
          exact
          path="/restaurant-placeorder/:id"
          component={RestaurantPlaceOrderScreen}
        />
        <ProtectedRoute
          exact
          path="/retailer-wholesaler-placeorder/:id"
          component={RetailerWholesalerPlaceOrderScreen}
        />
        <ProtectedRoute path="/my_profile" component={MyProfile} />
        <Route exact path="/invite">
          <Redirect to="/my_profile/invite_friends" />
        </Route>
        <ProtectedRoute exact path="/profile" component={UserProfile} />
        <ProtectedRoute exact path="/addresses" component={MyAddresses} />
        <ProtectedRoute exact path="/my_orders" component={UserOrders} />
        <ProtectedRoute exact path="/receipts" component={Receipts} />
        <ProtectedRoute exact path="/pay/authorize" component={CardPinForm} />
        <ProtectedRoute
          exact
          path="/v3/validate-charge"
          component={CardOtpForm}
        />
        <ProtectedRoute
          exact
          path="/cardOrder"
          component={CardPaymentDoneScreen}
        />
        <Route exact path="/orderDetail/:id" component={OrderDetail} />
        {/* Report an issue and follow it. Both are the customer's own cases, so
            both are behind ProtectedRoute like /my_orders and /receipts. The
            list route must stay exact or it would swallow the detail one. */}
        <ProtectedRoute exact path="/support-cases" component={SupportCasesPage} />
        <ProtectedRoute
          exact
          path="/support-cases/:caseId"
          component={SupportCaseDetailPage}
        />
        <Route exact path="/promotions" component={Promotions} />
        {/* OAuth 2.0 PKCE consent screen — for AI client identity linking */}
        <Route exact path="/oauth/consent" component={OAuthConsentPage} />
        {/* Blog / Insights */}
        <Route exact path="/blog" component={BlogListPage} />
        <Route exact path="/blog/:slug" component={BlogArticlePage} />
        {/* Catch-all: any unmatched path (e.g. a stale/indexed "/home" URL that
            Netlify serves index.html for with a 200) would otherwise render a
            blank page. Redirect unknown paths to the landing page instead. */}
        <Route render={() => <Redirect to="/" />} />
        </Switch>
        <ToastContainer />
      </Router>
    </GoogleMapsProvider>
  );
};

export default App;
